import { differenceBy, findIndex, kebabCase } from 'lodash';

import { createReducer } from 'utils/redux';
import { API, get, put } from 'utils/api';

const RECEIVE = 'userNotifications/RECEIVE';
const LOADING = 'userNotifications/LOADING';
const RESET = 'userNotifications/RESET';

import type {
  Action,
  Campaign,
  Dispatch,
  GetState,
  ThunkAction,
  UserNotification,
  UserNotificationGlobalMeta,
  UserNotificationState,
  UserNotificationType,
  UserNotificationUserMeta
} from 'types';

const { CAMPAIGNS } = API.END_POINTS;

const initialState: UserNotificationState = {
  notifications: [],
  collectionNotifications: {},
  globalCampaignNotificationSettings: {},
  isLoading: true
};

function receiveNotifications(
  notifications: Array<UserNotification>,
  collectionNotifications: UserNotificationGlobalMeta,
  globalCampaignNotificationSettings: UserNotificationGlobalMeta
): Action {
  return {
    type: RECEIVE,
    payload: {
      notifications,
      collectionNotifications,
      globalCampaignNotificationSettings
    }
  };
}

export function resetNotifications(): Action {
  return {
    type: RESET,
    payload: {}
  };
}

function composeCampaignsNotifications(
  campaigns: Array<Campaign>,
  campaignSettings: UserNotificationUserMeta,
  globalSettings: UserNotificationGlobalMeta
): Array<UserNotification> {
  const defaultAllowNotifications: boolean = globalSettings.defaultStatus === 'enabled';

  const composedNotifications: Array<UserNotification> = campaigns.map((campaign) => {
    const brandProfile = campaign.brandProfile || {};

    const notification: UserNotification = {
      campaign,
      id: campaign.id,
      key: brandProfile.name ? kebabCase(brandProfile.name.toLowerCase()) : '',
      allowNotifications: defaultAllowNotifications,
      hasSharedAccess: campaign.hasSharedAccess
    };

    if (campaignSettings[campaign.id]) {
      notification.allowNotifications = campaignSettings[campaign.id].status === 'enabled';
    }

    return notification;
  });

  return composedNotifications;
}


export function loadNotifications(userId: number): ThunkAction {
  return (dispatch: Dispatch) => {
    const url = `${CAMPAIGNS.manageCampaignNotificationSettings()}?user_id=${userId}`;

    return get(url)
      .then((response) => {
        const { data, meta } = response.data;
        const { collections } = meta;
        const { globalCampaignNotificationSettings, userCampaignNotificationSettings } = meta;

        const campaignNotifications = composeCampaignsNotifications(data, userCampaignNotificationSettings, globalCampaignNotificationSettings);

        dispatch(receiveNotifications(campaignNotifications, collections, globalCampaignNotificationSettings));
      });
  };
}

export function updateNotificationSettingsGroup(
  updatedNotifications: Array<UserNotification>,
  updatedCollectionNotifications: UserNotificationUserMeta,
  type: UserNotificationType
): ThunkAction {
  const url = CAMPAIGNS.manageCampaignNotificationSettings();

  return (dispatch: Dispatch, getState: GetState) => {
    const allNotifications = [...getState().userNotifications.notifications];
    const allCollectionNotifications = { ...getState().userNotifications.collectionNotifications };
    const globalCampaignNotificationSettings = getState().userNotifications.globalCampaignNotificationSettings;
    const filteredNotifications = differenceBy(allNotifications, updatedNotifications, 'key');

    const composedCollectionNotifications = {
      ...allCollectionNotifications,
      updatedCollectionNotifications
    };

    dispatch(receiveNotifications(filteredNotifications.concat(updatedNotifications), composedCollectionNotifications, globalCampaignNotificationSettings));

    const data = updatedNotifications.map(notification => ({
      notificationGroupName: globalCampaignNotificationSettings.id,
      resourceId: notification.id,
      type,
      status: notification.allowNotifications ? 'enabled' : 'disabled'
    }));

    return put(url, { data });
  };
}

export function updateNotificationSettingsSingle(
  id: number,
  allowNotifications: boolean,
  type: UserNotificationType
): ThunkAction {
  const url = CAMPAIGNS.manageCampaignNotificationSettings();

  return (dispatch: Dispatch, getState: GetState) => {
    const updatedCampaignNotifications = [...getState().userNotifications.notifications];
    const updatedCollectionNotifications = { ...getState().userNotifications.collectionNotifications };
    const globalCampaignNotificationSettings = getState().userNotifications.globalCampaignNotificationSettings;

    if (type === 'campaign') {
      const index = findIndex(updatedCampaignNotifications, n => n.id === id);

      updatedCampaignNotifications[index] = {
        ...updatedCampaignNotifications[index],
        allowNotifications
      };
    }

    if (type === 'creator_proposal') {
      updatedCollectionNotifications[id] = {
        ...updatedCollectionNotifications[id],
        status: allowNotifications ? 'enabled' : 'disabled'
      };
    }

    dispatch(receiveNotifications(updatedCampaignNotifications, updatedCollectionNotifications, globalCampaignNotificationSettings));

    const data = {
      notificationGroupName: globalCampaignNotificationSettings.id,
      resourceId: id,
      type,
      status: allowNotifications ? 'enabled' : 'disabled'
    };

    return put(url, { data: [data] });
  };
}

export default createReducer<UserNotificationState, any>('userNotifications', initialState, {
  [RESET]: () => initialState,
  [LOADING]: (state, { status }) => ({
    ...state,
    isLoading: status
  }),
  [RECEIVE]: (state, { notifications, collectionNotifications, globalCampaignNotificationSettings }) => ({
    notifications,
    collectionNotifications,
    globalCampaignNotificationSettings,
    isLoading: false
  })
});
